import React, { useContext, useMemo } from "react";
import Script from "next/script";
import { Slot } from "utils/create-slots";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/DropdownMenu/DropdownMenu";
import { Icon } from "@/components/Icon";
import Highlights from "@/components/Highlights/Highlights";
import Keyfacts from "@/components/Keyfacts/Keyfacts";
import Car from "@/data/car/car";
import * as gtag from "@/lib/gtag";
import useSalesforce from "hooks/useSalesforce";
import { useIsomorphicLayoutEffect } from "hooks/useIsomorphicLayoutEffect";
import { CalculatorProvider } from "@/modules/BDK_Calculator/CalculatorContext";
import CalculatorRoot from "@/modules/BDK_Calculator/CalculatorRoot";
import { formatPrice } from "@/modules/BDK_Calculator/lib/formatFunctions";
import CommerceBox from "@/modules/CarDetail/components/CommerceBox";
import Gallery from "../components/Gallery";
import Specs from "../components/Specs";
import PDFLink from "../components/PDFLink";
import FinanceInfoText from "../components/FinanceInfoText";
import CtaSection from "@/modules/CtaSection/CtaSection";
import CtaSectionCommerceBox from "@/modules/CarDetail/components/CtaSectionCommerceBox";
import EnergyFootprint from "@/modules/EnergyFootprint/EnergyFootprint";
import NativeShare from "@/modules/NativeShare/NativeShare";
import UspBarStatic from "@/modules/UspBarStatic/UspBarStatic";
import { WishlistContext } from "@/modules/Wishlist/WishlistProvider";
import { PageContext } from "pages/[[...slug]]";
import { addTypeCarJsonLd, addTypeProductJsonLd } from "../helpers";

const CarDetail = ({ content }) => {
  let financedata;
  const baseUrl = process.env.NEXT_PUBLIC_FRONTEND_URL;
  const { pushEvent } = useSalesforce();
  const { data } = content;
  const { breadcrumbs, meta } = useContext(PageContext);
  const car = useMemo(() => Car.from(data), [data]);
  const { isOnWishlist, addToWishlist, removeFromWishlist } =
    useContext(WishlistContext);
  const { tablist } = data;
  // Include all cars with a price over 120.000€ dirty way for specific texts in akkordeon and showen CTA's:
  const isIncluded = data.preis < 120000;
  const SHARE_TEXT = "Schau Dir mal diese Auto an: \n";
  const SHARE_LINK = `${
    typeof baseUrl === "string" && baseUrl.replace(/\/$/, "")
  }${meta.canonical}`;
  const SHARE_SUBJECT = "Schau Dir mal diese Auto an: \n";
  const shareData = {
    title: `${data.name}` || "",
    text: SHARE_TEXT,
    url: SHARE_LINK,
    subject: SHARE_SUBJECT,
  };

  useIsomorphicLayoutEffect(() => {
    if (typeof window !== "undefined") {
      if (window.localStorage.getItem("calculatorData")) {
        window.localStorage.removeItem("calculatorData");
      }
      if (window.localStorage.getItem("calculatorDataRate")) {
        window.localStorage.removeItem("calculatorDataRate");
      }
    }
    const timeout = setTimeout(() => {
      pushEvent("trackPageView", {
        item: data.internalid,
      });

      gtag.event({
        event: "view_item",
        ecommerce: {
          transaction_id:
            `T_${car?.internalid}_${Math.floor(Math.random() * 1000)}` || "",
          value: car?.preis || "",
          currency: "EUR",
          items: [
            {
              item_id: car?.internalid || "",
              item_name: car?.title || "",
              item_brand: car?.marke || "",
              item_category: car?.vehicleType || "",
              item_category2: car?.usagecategory || "",
              item_category3: car?.getriebeart || "",
              item_category4: car?.kraftstoffart || "",
              item_variant: car?.farbe || "",
              price: car?.preis || "",
              quantity: 1,
            },
          ],
        },
      });
    }, 3000);

    return () => clearTimeout(timeout);
  }, []);

  if (typeof data.financingdata !== "undefined") {
    financedata =
      typeof data.financingdata === "object"
        ? data.financingdata
        : JSON.parse(data.financingdata);
  }

  const getMonthlyRateFinancing = (obj, showNetto) => {
    if (Object.keys(obj).length === 0 && obj.constructor === Object) {
      return " Error in API - Please Check";
    } else {
      if (showNetto) {
        return `${formatPrice(
          financedata?.response?.monthlyInstallmentTotalAmount?.netValue
        )}`;
      } else {
        return `${formatPrice(
          financedata?.response?.monthlyInstallmentTotalAmount?.grossValue
        )}`;
      }
    }
  };

  if (!car) return null;

  return (
    <>
      <Script
        id="json-ld-type-Car"
        type="application/ld+json"
        dangerouslySetInnerHTML={addTypeCarJsonLd(car)}
      />
      <Script
        id="json-ld-type-Product"
        type="application/ld+json"
        dangerouslySetInnerHTML={addTypeProductJsonLd(car)}
      />
      <div className="container">
        <div className="flex justify-between lg:pt-8 lg:pb-7 py-6">
          <Breadcrumbs
            items={breadcrumbs}
            className="!ml-0 !pl-0 md:w-2/3 w-full lg:pt-7 pt-2"
          >
            {breadcrumbs.map(({ link, title }) => (
              <Breadcrumbs.Item href={link} key="link">
                {title}
              </Breadcrumbs.Item>
            ))}
          </Breadcrumbs>
          <div className=" lg:flex hidden items-end gap-6">
            <button
              className="flex group"
              onClick={(e) => {
                e.preventDefault();
                if (isOnWishlist(car.uid)) {
                  removeFromWishlist(car.uid);
                } else {
                  addToWishlist(car.uid);
                  gtag.event({
                    event: "add_to_cart",
                    ecommerce: {
                      value: car.preis || "",
                      currency: "EUR",
                      items: [
                        {
                          item_id: car.internalid || "",
                          item_name: `${data.title} ${car.title_sub}` || "",
                          item_brand: car.marke || "",
                          item_category: car.vehicleType || "",
                          item_category2: car.usagecategory || "",
                          item_category3: car.getriebeart || "",
                          item_category4: car.kraftstoffart || "",
                          item_variant: car.farbe || "",
                          price: car.preis || "",
                          quantity: 1,
                        },
                      ],
                    },
                  });
                }
              }}
            >
              <span className="font-bold text-base text-blue-light hover:to-blue-medium">
                Merken
              </span>
              <Icon
                type={isOnWishlist(car.uid) ? "star-filled" : "star"}
                className="w-6 h-6 xl:w-7 xl:h-7 text-blue-light group-hover:text-blue-medium -mt-1 ml-3"
                purposeLabel="Zur Merkliste hinzufügen"
              />
            </button>

            <NativeShare
              shareData={shareData}
              linkedin={true}
              facebook={true}
              whatsapp={false}
            >
              <div className="flex border-l-[1px] border-gray-300 pl-6 group">
                <span className="font-bold text-base text-blue-light group-hover:to-blue-medium">
                  Teilen
                </span>
                <Icon
                  type="share"
                  className="text-blue-light group-hover:text-blue-medium w-6 h-6 xl:w-7 xl:h-7 ml-3 -mt-1"
                  purposeLabel="Teilen"
                />
              </div>
            </NativeShare>
          </div>
        </div>
      </div>
      <div className="sm:container mx-auto">
        <Gallery
          assets={data.assets}
          link={data.link.request}
          showStopper={data.showTroubleBadge}
        >
          <Slot name="wishlistMobile">
            <span
              className=" top-5 right-5 absolute block lg:hidden"
              onClick={(e) => {
                e.preventDefault();
                if (isOnWishlist(car.uid)) {
                  removeFromWishlist(car.uid);
                } else {
                  addToWishlist(car.uid);

                  gtag.event({
                    event: "add_to_cart",
                    ecommerce: {
                      value: car.preis || "",
                      currency: "EUR",
                      items: [
                        {
                          item_id: car.internalid || "",
                          item_name: `${data.title} ${car.title_sub}` || "",
                          item_brand: car.marke || "",
                          item_category: car.vehicleType || "",
                          item_category2: car.usagecategory || "",
                          item_category3: car.getriebeart || "",
                          item_category4: car.kraftstoffart || "",
                          item_variant: car.farbe || "",
                          price: car.preis || "",
                          quantity: 1,
                        },
                      ],
                    },
                  });
                }
              }}
            >
              <Icon
                type={isOnWishlist(car.uid) ? "star-filled" : "star"}
                className="w-8 h-8 text-blue-medium"
                purposeLabel="Zur Merkliste hinzufügen"
              />
            </span>
          </Slot>
        </Gallery>
      </div>
      <CommerceBox isfinancable={car.isfinanceable} isleasable={car.isleasable}>
        <Slot name="title">
          <h1 className="text-blue-dark font-bold xl:text-4xl text-3xl truncate">
            {car.title}
          </h1>
          <p className="font-extralight text-gray-900 lg:pb-0 lg:mt-4 pb-2 xl:text-3xl text-sm truncate lg:max-w-[220px] xl:max-w-[350px]">
            {car.title_sub}
          </p>
        </Slot>
        <Slot name="price">
          {car.formattedPrice}{" "}
          {car.showNettoPreis && (
            <span className="lg:text-sm text-xs ml-2">zzgl. MwSt.</span>
          )}
        </Slot>
        <Slot name="monthlyPrice">
          {car.isfinanceable &&
            getMonthlyRateFinancing(financedata, car.showNettoPreis)}
          {car.showNettoPreis && (
            <span className="lg:text-sm text-xs ml-2">zzgl. MwSt.</span>
          )}
        </Slot>
        {data.rabatt && <Slot name="discount">-{car.rabatt}% Rabatt*</Slot>}

        <Slot name="shareMobile">
          <NativeShare
            shareData={shareData}
            linkedin={true}
            facebook={true}
            whatsapp={true}
          >
            <Icon
              type="share"
              className="text-blue-light hover:text-blue-medium w-7 h-7"
              purposeLabel="Teilen"
            />
          </NativeShare>
        </Slot>

        {car.isfinanceable && (
          <Slot name="leasingInfo">
            <DropdownMenu>
              <DropdownMenuTrigger className="ml-3 mb-auto lg:block hidden ">
                <Icon
                  type="info"
                  className="xl:w-[20px] w-6 h-6 xl:h-[20px] text-blue-dark hover:text-blue-light"
                  purposeLabel="Info"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <div className="flex justify-evenly !z-[800] p-5 gap-4  bg-white text-blue-dark shadow-lg rounded-4 flex-wrap 2xl:max-w-[350px] lg:max-w-[286px] text-sm">
                  <FinanceInfoText
                    financingdata={data.financingdata}
                    showNettoPreis={data.showNettoPreis}
                  />
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </Slot>
        )}
      </CommerceBox>

      <CtaSectionCommerceBox
        link={data.link.request}
        carOfferNumber={car.internalid}
        carMassAndWeightLink={data.link.table || ""}
      />

      <UspBarStatic />
      <Keyfacts label="Ausstattungsmerkmale">
        {car.keyfacts.map(({ label, value }, index) => (
          <Keyfacts.Fact label={label} key={`keyfact-${index}`}>
            {value}
          </Keyfacts.Fact>
        ))}
      </Keyfacts>

      <Specs tablist={tablist} />
      {data.specialEquipment.length !== 0 && (
        <Highlights label="Highlights">
          {data.specialEquipment.length <= 3
            ? data.specialEquipment.map((highlight: string) => (
                <Highlights.Fact key={highlight}>{highlight}</Highlights.Fact>
              ))
            : data.specialEquipment
                .slice(2, 5)
                .map((highlight: string) => (
                  <Highlights.Fact key={highlight}>{highlight}</Highlights.Fact>
                ))}
        </Highlights>
      )}
      <div className="container md:p-2 xl:p-7">
        <PDFLink link={data.link.print} title={car.title} />
      </div>
      <EnergyFootprint>
        <EnergyFootprint.Table label="Kraftstoffverbrauch**">
          {car?.WLTP &&
            Object.entries(car?.WLTP).map(([key, value], index) => (
              <EnergyFootprint.TableRow
                label={key}
                key={`energy-${index}`}
                value={value.toString()}
              />
            ))}
        </EnergyFootprint.Table>
        {car?.WLTP?.Energieeffizienz && (
          <EnergyFootprint.Chart energyClass={car.WLTP.Energieeffizienz} />
        )}
      </EnergyFootprint>
      <CtaSection
        className="lg:py-10  pb-9 lg:mb-0 mb-10"
        link={data.link.request}
        includeLinkText={isIncluded}
      />
      {data && (car.isfinanceable || car.isleasable) && (
        <CalculatorProvider>
          <CalculatorRoot data={data} />
        </CalculatorProvider>
      )}
    </>
  );
};

export default CarDetail;
